import { ChangeDetectionStrategy, Component, computed, input, model, output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { EmailMessage } from "../../../services/email.message.service";
import { S25PipesModule } from "../../../pipes/s25.pipes.module";
import { S25LoadingInlineModule } from "../../s25-loading/s25.loading.inline.module";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { S25RichTextEditorModule } from "../../s25-rich-text-editor/s25.rich.text.editor.module";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import {
    EMAIL_SENT_DATE_PART_FORMAT,
    EMAIL_SENT_TIME_PART_FORMAT,
    getMessageSubject,
    getRecipientListSummary,
    getThreadMessageFromLabel,
    getThreadMessagePreview,
    messageHasQuotedReplyHtml,
    stripHtmlToPlainText,
} from "./email.sent.util";

/**
 * Thread detail pane for Connect25 Sent mail and Event Email History.
 * Parent owns selection, expansion, quoted-text visibility, mark-read, and send; this component renders
 * messages and emits user actions (expand/collapse, show quoted text, close/expand layout, reply).
 */
@TypeManagerDecorator("s25-ng-se-email-sent-detail")
@Component({
    selector: "s25-ng-se-email-sent-detail",
    host: { class: "email-sent-detail" },
    template: `
        @if (!convoUuid()) {
            <div class="detail-empty" role="status">
                <p>Select a conversation</p>
            </div>
        } @else if (isLoading()) {
            <div class="detail-status" role="status">
                <s25-ng-loading-inline-static />
                <span>Loading conversation...</span>
            </div>
        } @else if (!messages().length) {
            <div class="detail-empty" role="status">
                <p>No messages in this conversation.</p>
            </div>
        } @else {
            @if (headerMessage(); as header) {
                <div class="detail-panel__subject-block">
                    <div class="detail-panel__subject-row">
                        <h2 class="detail-panel__subject">{{ messageSubject(header) }}</h2>
                        <div class="detail-panel__header-actions">
                            @if (canReply() && !replyComposerOpen()) {
                                <button
                                    type="button"
                                    class="reply-trigger-btn reply-trigger-btn--header"
                                    (click)="openReplyComposer()"
                                >
                                    Reply
                                </button>
                            }
                            @if (showSideToolbar()) {
                                <div class="detail-panel__toolbar-actions">
                                    <button
                                        type="button"
                                        class="detail-icon-btn"
                                        aria-label="Open full screen"
                                        (click)="expandFullscreen.emit()"
                                    >
                                        <s25-ng-icon [type]="'expand'" [size]="16" [ariaLabel]="false" />
                                    </button>
                                    <button
                                        type="button"
                                        class="detail-icon-btn"
                                        aria-label="Close conversation"
                                        (click)="closeDetail.emit()"
                                    >
                                        <s25-ng-icon [type]="'close'" [size]="16" [ariaLabel]="false" />
                                    </button>
                                </div>
                            }
                        </div>
                    </div>
                    <p class="detail-panel__subtitle">
                        @if (header.received_at) {
                            <span class="detail-panel__datetime">
                                <time [attr.datetime]="header.received_at">
                                    {{ header.received_at | date: datePartFormat() }}
                                </time>
                                <time [attr.datetime]="header.received_at">
                                    {{ header.received_at | date: timePartFormat() }}
                                </time>
                            </span>
                        }
                        @if (isMultiMessageThread()) {
                            <span> {{ threadCountLabel() }}</span>
                        }
                    </p>
                    @if (originalMessage(); as threadOriginal) {
                        <div class="detail-panel__addresses">
                            <div class="detail-panel__address-row">
                                <span class="detail-panel__address-label">From</span>
                                <span>{{ threadFromLabel(threadOriginal) }}</span>
                            </div>
                            <div class="detail-panel__address-row">
                                <span class="detail-panel__address-label">To</span>
                                <div class="detail-panel__recipient-chips">
                                    @for (
                                        recipientLabel of threadRecipientSummary(threadOriginal).all;
                                        track recipientLabel
                                    ) {
                                        <span class="recipient-chip">{{ recipientLabel }}</span>
                                    }
                                </div>
                            </div>
                        </div>
                    }
                    @if (canReply() && replyComposerOpen()) {
                        <div class="reply-composer reply-composer--header">
                            <div class="reply-composer__field">
                                <span class="reply-composer__label">To</span>
                                <div class="reply-composer__recipient-chips">
                                    @for (recipientLabel of replyToLabels(); track recipientLabel) {
                                        <span class="recipient-chip">{{ recipientLabel }}</span>
                                    }
                                </div>
                            </div>
                            @if (replyHint()) {
                                <p class="reply-composer__hint">{{ replyHint() }}</p>
                            }
                            <div class="reply-composer__field">
                                <span class="reply-composer__label">Subject</span>
                                <span class="reply-composer__subject">{{ replySubject() }}</span>
                            </div>
                            <label class="reply-composer__field reply-composer__field--body">
                                <span class="reply-composer__label">Message</span>
                                <s25-ng-rich-text-editor
                                    [modelValue]="replyBody()"
                                    (modelValueChange)="replyBody.set($event)"
                                    [autoResize]="true"
                                />
                            </label>
                            <div class="reply-composer__actions">
                                <s25-ng-button
                                    type="outline"
                                    buttonText="Cancel"
                                    [disabled]="replySending()"
                                    (clicked)="cancelReplyComposer()"
                                />
                                <s25-ng-button
                                    type="primary"
                                    [buttonText]="replySending() ? 'Sending...' : 'Send'"
                                    [disabled]="!canSendReply()"
                                    (clicked)="submitReply()"
                                />
                            </div>
                        </div>
                    }
                </div>
            }

            <div class="thread-messages" [class.thread-messages--single]="!isMultiMessageThread()">
                @if (isMultiMessageThread()) {
                    @for (message of replyMessages(); track message.id) {
                        <article
                            class="thread-message-card"
                            [class.thread-message-card--collapsed]="!isMessageExpanded(message)"
                        >
                            <button
                                type="button"
                                class="thread-message-card__header"
                                [attr.aria-expanded]="isMessageExpanded(message)"
                                (click)="toggleExpanded.emit(message)"
                            >
                                <s25-ng-icon
                                    class="thread-message-card__chevron"
                                    [class.thread-message-card__chevron--expanded]="isMessageExpanded(message)"
                                    [type]="'caretRight'"
                                    [size]="14"
                                    [ariaLabel]="false"
                                />
                                @if (isMessageExpanded(message)) {
                                    <div class="thread-message-card__addresses">
                                        <div class="thread-message-card__address-row">
                                            <span class="thread-message-card__label">From</span>
                                            <span>{{ threadFromLabel(message) }}</span>
                                        </div>
                                        <div class="thread-message-card__address-row">
                                            <span class="thread-message-card__label">To</span>
                                            <div class="thread-message-card__recipient-chips">
                                                @for (
                                                    recipientLabel of threadRecipientSummary(message).all;
                                                    track recipientLabel
                                                ) {
                                                    <span class="recipient-chip">{{ recipientLabel }}</span>
                                                }
                                            </div>
                                        </div>
                                    </div>
                                    @if (message.received_at) {
                                        <span class="thread-message-card__datetime">
                                            <time
                                                class="thread-message-card__date"
                                                [attr.datetime]="message.received_at"
                                            >
                                                {{ message.received_at | date: datePartFormat() }}
                                            </time>
                                            <time
                                                class="thread-message-card__time"
                                                [attr.datetime]="message.received_at"
                                            >
                                                {{ message.received_at | date: timePartFormat() }}
                                            </time>
                                        </span>
                                    }
                                } @else {
                                    <span class="thread-message-card__sender">{{ threadFromLabel(message) }}</span>
                                    @if (message.received_at) {
                                        <span class="thread-message-card__datetime">
                                            <time
                                                class="thread-message-card__date"
                                                [attr.datetime]="message.received_at"
                                            >
                                                {{ message.received_at | date: datePartFormat() }}
                                            </time>
                                            <time
                                                class="thread-message-card__time"
                                                [attr.datetime]="message.received_at"
                                            >
                                                {{ message.received_at | date: timePartFormat() }}
                                            </time>
                                        </span>
                                    }
                                    @if (threadPreview(message)) {
                                        <span class="thread-message-card__preview">{{ threadPreview(message) }}</span>
                                    }
                                }
                            </button>
                            @if (isMessageExpanded(message)) {
                                <div
                                    class="thread-message-card__body"
                                    [class.thread-message-card__body--hide-quoted]="shouldHideQuoted(message)"
                                    [innerHTML]="message.body ?? '' | safeHTML"
                                ></div>
                                @if (messageHasQuoted(message) && shouldHideQuoted(message)) {
                                    <button
                                        type="button"
                                        class="thread-message-card__show-quoted"
                                        (click)="showQuoted.emit(message)"
                                    >
                                        Show quoted text
                                    </button>
                                }
                            }
                        </article>
                    }

                    @if (replyMessages().length && originalMessage()) {
                        <div class="thread-original-divider" role="separator">
                            <span>Original message</span>
                        </div>
                    }

                    @if (originalMessage(); as original) {
                        <article
                            class="thread-message-card thread-message-card--original"
                            [class.thread-message-card--collapsed]="!isMessageExpanded(original)"
                        >
                            <button
                                type="button"
                                class="thread-message-card__header"
                                [attr.aria-expanded]="isMessageExpanded(original)"
                                (click)="toggleExpanded.emit(original)"
                            >
                                <s25-ng-icon
                                    class="thread-message-card__chevron"
                                    [class.thread-message-card__chevron--expanded]="isMessageExpanded(original)"
                                    [type]="'caretRight'"
                                    [size]="14"
                                    [ariaLabel]="false"
                                />
                                @if (isMessageExpanded(original)) {
                                    <div class="thread-message-card__addresses">
                                        <div class="thread-message-card__address-row">
                                            <span class="thread-message-card__label">From</span>
                                            <span>{{ threadFromLabel(original) }}</span>
                                        </div>
                                        <div class="thread-message-card__address-row">
                                            <span class="thread-message-card__label">To</span>
                                            <div class="thread-message-card__recipient-chips">
                                                @for (
                                                    recipientLabel of threadRecipientSummary(original).all;
                                                    track recipientLabel
                                                ) {
                                                    <span class="recipient-chip">{{ recipientLabel }}</span>
                                                }
                                            </div>
                                        </div>
                                    </div>
                                    @if (original.received_at) {
                                        <span class="thread-message-card__datetime">
                                            <time
                                                class="thread-message-card__date"
                                                [attr.datetime]="original.received_at"
                                            >
                                                {{ original.received_at | date: datePartFormat() }}
                                            </time>
                                            <time
                                                class="thread-message-card__time"
                                                [attr.datetime]="original.received_at"
                                            >
                                                {{ original.received_at | date: timePartFormat() }}
                                            </time>
                                        </span>
                                    }
                                } @else {
                                    <span class="thread-message-card__sender">{{
                                        threadOriginalSenderLabel(original)
                                    }}</span>
                                    @if (original.received_at) {
                                        <span class="thread-message-card__datetime">
                                            <time
                                                class="thread-message-card__date"
                                                [attr.datetime]="original.received_at"
                                            >
                                                {{ original.received_at | date: datePartFormat() }}
                                            </time>
                                            <time
                                                class="thread-message-card__time"
                                                [attr.datetime]="original.received_at"
                                            >
                                                {{ original.received_at | date: timePartFormat() }}
                                            </time>
                                        </span>
                                    }
                                    @if (threadPreview(original)) {
                                        <span class="thread-message-card__preview">{{ threadPreview(original) }}</span>
                                    }
                                }
                            </button>
                            @if (isMessageExpanded(original)) {
                                <div
                                    class="thread-message-card__body"
                                    [innerHTML]="original.body ?? '' | safeHTML"
                                ></div>
                            }
                        </article>
                    }
                } @else if (originalMessage(); as singleMessage) {
                    <div class="thread-message-flat">
                        <div
                            class="thread-message-flat__body"
                            [class.thread-message-flat__body--hide-quoted]="shouldHideQuoted(singleMessage)"
                            [innerHTML]="singleMessage.body ?? '' | safeHTML"
                        ></div>
                        @if (messageHasQuoted(singleMessage) && shouldHideQuoted(singleMessage)) {
                            <button
                                type="button"
                                class="thread-message-card__show-quoted"
                                (click)="showQuoted.emit(singleMessage)"
                            >
                                Show quoted text
                            </button>
                        }
                    </div>
                }
            </div>
        }
    `,
    styles: `
        :host {
            display: flex;
            flex-direction: column;
            flex: 1;
            min-height: 0;
        }

        .detail-empty,
        .detail-status {
            padding: 2rem 1rem;
            text-align: center;
            color: var(--color--text-muted);
        }

        .detail-status {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 0.75rem;
        }

        .detail-panel__toolbar-actions {
            display: flex;
            align-items: center;
            gap: 0.25rem;
            flex-shrink: 0;
        }

        .detail-panel__header-actions {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            flex-shrink: 0;
        }

        .detail-icon-btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 0.375rem;
            border: none;
            border-radius: var(--border-radius);
            background: none;
            color: var(--color--text-muted);
            cursor: pointer;
            transition:
                background var(--transition-medium) ease,
                color var(--transition-medium) ease;
        }

        .detail-icon-btn:hover {
            background: var(--color--surface-2-bg-hover);
            color: var(--color--text);
        }

        .detail-panel__subject-block {
            padding: 1.25rem 1.5rem 1rem;
            border-bottom: 1px solid color-mix(in srgb, var(--color--border-disabled) 60%, transparent);
        }

        .detail-panel__subject-row {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 0.75rem;
        }

        .detail-panel__subject {
            flex: 1;
            min-width: 0;
            margin: 0;
            font-size: 1rem;
            font-weight: 600;
            line-height: 1.4;
            color: var(--color--text-strong);
        }

        .detail-panel__subtitle {
            margin: 0.25rem 0 0;
            font-size: 0.75rem;
            color: var(--color--text-muted);
        }

        .detail-panel__datetime {
            display: inline-flex;
            align-items: baseline;
            gap: 0.25rem;
        }

        .detail-panel__addresses {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            margin-top: 1rem;
        }

        .detail-panel__address-row {
            display: grid;
            grid-template-columns: 3rem minmax(0, 1fr);
            gap: 0.5rem;
            align-items: start;
            font-size: 0.8125rem;
            line-height: 1.4;
        }

        .detail-panel__address-label {
            font-weight: 600;
            color: var(--color--text-muted);
        }

        .detail-panel__recipient-chips {
            display: flex;
            flex-wrap: wrap;
            gap: 0.375rem;
            padding-bottom: 0.125rem;
        }

        .recipient-chip {
            display: inline-flex;
            align-items: center;
            max-width: 100%;
            padding: 0.125rem 0.5rem;
            border-radius: var(--border-radius);
            background: var(--color--surface-2-bg);
            border: 1px solid var(--color--border-disabled);
            font-size: 0.75rem;
            color: var(--color--text);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .thread-messages {
            display: flex;
            flex-direction: column;
            gap: 1rem;
            padding: 0 1.5rem 1.5rem;
        }

        .thread-messages--single {
            gap: 0;
        }

        .thread-message-flat__body {
            font-size: 0.875rem;
            line-height: 1.65;
            word-break: break-word;
        }

        .thread-message-flat__body--hide-quoted ::ng-deep #divRplyFwdMsg,
        .thread-message-flat__body--hide-quoted ::ng-deep #appendonsend,
        .thread-message-flat__body--hide-quoted ::ng-deep hr,
        .thread-message-flat__body--hide-quoted ::ng-deep hr ~ * {
            display: none !important;
        }

        .thread-message-card {
            border: 1px solid color-mix(in srgb, var(--color--border-disabled) 60%, transparent);
            border-radius: 12px;
            background: var(--color--surface-bg);
            box-shadow: 0 1px 2px color-mix(in srgb, var(--color--text) 3%, transparent);
            overflow: hidden;
        }

        .thread-message-card--original {
            opacity: 0.88;
            transform: scale(0.995);
            transform-origin: top center;
            background: color-mix(in srgb, var(--color--page-bg) 80%, var(--color--surface-bg));
        }

        .thread-message-card__header {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.375rem 0.5rem;
            width: 100%;
            padding: 1rem 1.25rem 0.75rem;
            border: none;
            background: transparent;
            text-align: left;
            cursor: pointer;
            font: inherit;
            color: inherit;
        }

        .thread-message-card__header:hover {
            background: color-mix(in srgb, var(--color--surface-2-bg-hover) 50%, transparent);
        }

        .thread-message-card__chevron {
            flex-shrink: 0;
            color: var(--color--text-muted);
        }

        .thread-message-card__chevron--expanded {
            --icon--rotation-degrees: 90deg;
        }

        .thread-message-card__sender {
            font-size: 0.875rem;
            font-weight: 600;
            color: var(--color--text-strong);
        }

        .thread-message-card__datetime {
            display: inline-flex;
            align-items: baseline;
            gap: 0.25rem;
            margin-left: auto;
            flex-shrink: 0;
            font-size: 0.75rem;
            color: var(--color--text-muted);
        }

        .thread-message-card__date,
        .thread-message-card__time {
            font-size: inherit;
            color: inherit;
        }

        .thread-message-card__preview {
            flex-basis: 100%;
            padding-left: 1.375rem;
            font-size: 0.8125rem;
            color: var(--color--text-muted);
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }

        .thread-message-card__addresses {
            flex: 1;
            min-width: 0;
            display: flex;
            flex-direction: column;
            gap: 0.375rem;
            font-size: 0.8125rem;
            line-height: 1.4;
        }

        .thread-message-card__address-row {
            display: grid;
            grid-template-columns: 3rem minmax(0, 1fr);
            gap: 0.5rem;
            align-items: start;
        }

        .thread-message-card__label {
            font-weight: 600;
            color: var(--color--text-muted);
        }

        .thread-message-card__recipient-chips {
            display: flex;
            flex-wrap: wrap;
            gap: 0.375rem;
            padding-bottom: 0.125rem;
        }

        .thread-original-divider {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            margin: 0.25rem 0;
            font-size: 0.6875rem;
            font-weight: 600;
            letter-spacing: 0.04em;
            text-transform: uppercase;
            color: var(--color--text-muted);
        }

        .thread-original-divider::before,
        .thread-original-divider::after {
            content: "";
            flex: 1;
            height: 1px;
            background: var(--color--border-disabled);
        }

        .thread-message-card__body {
            padding: 0 1.25rem 1.25rem;
            font-size: 0.875rem;
            line-height: 1.65;
            word-break: break-word;
            border-top: none;
        }

        .thread-message-card__body--hide-quoted ::ng-deep #divRplyFwdMsg,
        .thread-message-card__body--hide-quoted ::ng-deep #appendonsend,
        .thread-message-card__body--hide-quoted ::ng-deep hr,
        .thread-message-card__body--hide-quoted ::ng-deep hr ~ * {
            display: none !important;
        }

        .thread-message-card__show-quoted {
            display: block;
            width: 100%;
            margin: 0.75rem 0 0;
            padding: 0;
            border: none;
            background: none;
            font-size: 0.8125rem;
            color: var(--color--link-text);
            cursor: pointer;
            text-align: center;
        }

        .thread-message-card__show-quoted:hover {
            text-decoration: underline;
            background: none;
        }

        @media (max-width: 768px) {
            .thread-message-card__label,
            .detail-panel__address-label {
                display: none;
            }

            .thread-message-card__address-row,
            .detail-panel__address-row {
                grid-template-columns: minmax(0, 1fr);
            }

            .thread-message-card__datetime {
                flex-basis: 100%;
                margin-left: 0;
                flex-direction: column;
                align-items: flex-end;
                gap: 0.125rem;
            }

            .detail-panel__datetime {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 0.125rem;
            }

            .detail-panel__subject-block {
                padding: 1rem 1rem 0.75rem;
            }

            .thread-messages {
                padding: 1rem;
            }
        }

        .reply-composer__hint {
            margin: 0;
            font-size: 0.8125rem;
            color: var(--color--text-muted);
        }

        .reply-trigger-btn {
            padding: 0.5rem 1rem;
            border: 1px solid var(--color--border-disabled);
            border-radius: var(--border-radius);
            background: var(--color--surface-bg);
            color: var(--color--link-text);
            font-size: 0.875rem;
            font-weight: 600;
            cursor: pointer;
            transition: background var(--transition-medium) ease;
        }

        .reply-trigger-btn--header {
            padding: 0.375rem 0.75rem;
            font-size: 0.8125rem;
            white-space: nowrap;
        }

        .reply-trigger-btn:hover {
            background: var(--color--surface-2-bg-hover);
        }

        .reply-composer {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
            padding: 1rem;
            border: 1px solid color-mix(in srgb, var(--color--border-disabled) 60%, transparent);
            border-radius: 12px;
            background: color-mix(in srgb, var(--color--page-bg) 35%, var(--color--surface-bg));
        }

        .reply-composer--header {
            margin-top: 1rem;
        }

        .reply-composer__field {
            display: grid;
            grid-template-columns: 4rem minmax(0, 1fr);
            gap: 0.5rem;
            align-items: start;
            font-size: 0.8125rem;
        }

        .reply-composer__field--body {
            display: flex;
            flex-direction: column;
            gap: 0.375rem;
        }

        .reply-composer__field--body s25-ng-rich-text-editor {
            display: block;
            width: 100%;
            min-width: 0;
        }

        .reply-composer__field--body ::ng-deep .tox-tinymce {
            width: 100% !important;
            max-width: 100%;
            box-sizing: border-box;
        }

        .reply-composer__label {
            font-weight: 600;
            color: var(--color--text-muted);
            padding-top: 0.125rem;
        }

        .reply-composer__subject {
            color: var(--color--text);
            word-break: break-word;
        }

        .reply-composer__recipient-chips {
            display: flex;
            flex-wrap: wrap;
            gap: 0.375rem;
        }

        .reply-composer__actions {
            display: flex;
            justify-content: flex-end;
            gap: 0.5rem;
            padding-top: 0.25rem;
        }

        @media (max-width: 768px) {
            .reply-composer__field {
                grid-template-columns: minmax(0, 1fr);
            }
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        S25PipesModule,
        S25LoadingInlineModule,
        S25IconComponent,
        S25RichTextEditorModule,
        S25ButtonComponent,
    ],
})
export class SeEmailSentDetailComponent {
    readonly convoUuid = input<string | undefined>();
    readonly isLoading = input(false);
    readonly messages = input<EmailMessage[]>([]);
    readonly headerMessage = input<EmailMessage | undefined>();
    readonly replyMessages = input<EmailMessage[]>([]);
    readonly originalMessage = input<EmailMessage | undefined>();
    readonly isMultiMessageThread = input(false);
    readonly threadCountLabel = input("");
    readonly expandedMessageIds = input<ReadonlySet<number>>(new Set());
    readonly showQuotedByMessageId = input<ReadonlySet<number>>(new Set());
    readonly showSideToolbar = input(false);
    readonly datePartFormat = input(EMAIL_SENT_DATE_PART_FORMAT);
    readonly timePartFormat = input(EMAIL_SENT_TIME_PART_FORMAT);
    readonly canReply = input(false);
    readonly replyHint = input("");
    readonly replySending = input(false);
    readonly replyToLabels = input<string[]>([]);
    readonly replySubject = input("");
    readonly replyComposerOpen = model(false);
    readonly replyBody = model("");

    readonly expandFullscreen = output<void>();
    readonly closeDetail = output<void>();
    readonly toggleExpanded = output<EmailMessage>();
    readonly showQuoted = output<EmailMessage>();
    readonly replySend = output<string>();

    readonly canSendReply = computed(
        () => !this.replySending() && !!stripHtmlToPlainText(this.replyBody()).length && !!this.replyToLabels().length,
    );

    openReplyComposer(): void {
        this.replyComposerOpen.set(true);
    }

    cancelReplyComposer(): void {
        this.replyComposerOpen.set(false);
        this.replyBody.set("");
    }

    submitReply(): void {
        if (!this.canSendReply()) {
            return;
        }
        this.replySend.emit(this.replyBody());
    }

    messageSubject(message: EmailMessage): string {
        return getMessageSubject(message);
    }

    threadFromLabel(message: EmailMessage): string {
        return getThreadMessageFromLabel(message);
    }

    /** Plain-text snippet for collapsed message headers in the thread view. */
    threadPreview(message: EmailMessage): string {
        return getThreadMessagePreview(message);
    }

    threadOriginalSenderLabel(message: EmailMessage): string {
        return message.contact_name_from ?? message.from_address ?? "You";
    }

    threadRecipientSummary(message: EmailMessage) {
        return getRecipientListSummary(message);
    }

    isMessageExpanded(message: EmailMessage): boolean {
        const messageId = message.id;
        if (messageId == null) {
            return false;
        }
        return this.expandedMessageIds().has(messageId);
    }

    /**
     * Whether quoted reply HTML should stay hidden via CSS. Checks showQuotedByMessageId first
     * (cheap Set lookup), then Outlook quoted-reply markers in the inbound message body.
     */
    shouldHideQuoted(message: EmailMessage): boolean {
        const messageId = message.id;
        if (messageId == null) {
            return false;
        }
        if (this.showQuotedByMessageId().has(messageId)) {
            return false;
        }
        return messageHasQuotedReplyHtml(message);
    }

    /** Template helper; quoted-reply detection lives in email.sent.util messageHasQuotedReplyHtml. */
    messageHasQuoted(message: EmailMessage): boolean {
        return messageHasQuotedReplyHtml(message);
    }
}
